import { useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { N700 } from "@atlaskit/theme/colors";

import {
    ufoEventPaidSignupConfluenceLandingPageLoad,
    ufoEventPaidSignupJSMLandingPageLoad,
    ufoEventPaidSignupJSWLandingPageLoad,
    ufoEventTryCloudSignupPageLoad,
    trackDataFoundationsAccountCreationComponent,
    ProductDataDetail,
    getPrimaryProductKeyFromBundle
} from "@/components/signup/utils";

import { SignupModuleWrapper } from "../../../signup-module-wrapper";
import { EmailForm, ExistingProductText } from "../components";
import { useSignupFormContext } from "../hooks";

export const DefaultSignupForm = () => {
    const {
        bundle,
        edition,
        showProductLogo,
        products,
        productName,
        isEmbedded
    } = useSignupFormContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    useEffect(() => {
        const dataFoundationsActionSubjectId = !!isEmbedded
            ? "embedded"
            : "standalone";

        trackDataFoundationsAccountCreationComponent(createAnalyticsEvent, {
            actionSubjectId: dataFoundationsActionSubjectId,
            productKey: getPrimaryProductKeyFromBundle(bundle),
            edition
        });
    }, [createAnalyticsEvent, bundle, edition, isEmbedded]);
    useEffect(() => {
        const customData = {
            webPlatform: "bxp-ui",
            reactComponent: "DefaultSignupForm",
            isLoggedIn: false
        };
        ufoEventTryCloudSignupPageLoad.success({
            customData
        });
        if (isEmbedded) {
            ufoEventPaidSignupJSWLandingPageLoad.success({
                customData: { reactComponent: "DefaultSignupForm" }
            });
            ufoEventPaidSignupConfluenceLandingPageLoad.success({
                customData: { reactComponent: "DefaultSignupForm" }
            });
            ufoEventPaidSignupJSMLandingPageLoad.success({
                customData: { reactComponent: "DefaultSignupForm" }
            });
        } else {
            ufoEventTryCloudSignupPageLoad.success({
                customData
            });
        }
    }, []);

    return (
        <SignupModuleWrapper>
            <div css={containerStyle} data-testid="signup-form">
                {showProductLogo && <ProductLogos products={products} />}
                <h1>
                    {products.length > 1 ? (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.headerBundle"
                            defaultMessage="Get started"
                        />
                    ) : (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.header"
                            defaultMessage="Get started with {product}"
                            values={{ product: productName }}
                        />
                    )}
                </h1>
                <h2>
                    {bundle === "jira-service-management-jira-software" ? (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.subheaderJSWJSM"
                            defaultMessage="It's free for up to 3 agents and 10 users."
                        />
                    ) : bundle === "jira-service-management" ? (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.subheaderJSM"
                            defaultMessage="Free for up to 3 agents"
                        />
                    ) : bundle === "jira-software" && edition === "standard" ? (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.subheader.jsw.standard"
                            defaultMessage="14-day free trial for unlimited users on the Standard plan — no credit card needed"
                        />
                    ) : bundle === "jira-software" && edition === "premium" ? (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.subheader.jsw.premium"
                            defaultMessage="30-day free trial for unlimited users on the Premium plan — no credit card needed"
                        />
                    ) : (
                        <FormattedMessage
                            id="BxpUiSignup.SignupForm.subheader.1"
                            defaultMessage="It's free for up to 10 users — no credit card needed."
                        />
                    )}
                </h2>
                <EmailForm />
                <ExistingProductText productName={productName} />
            </div>
        </SignupModuleWrapper>
    );
};

type ProductLogosProps = {
    products: ProductDataDetail[];
};

const ProductLogos = ({ products }: ProductLogosProps) => {
    const [baseProduct, ...bundledProducts] = products;

    if (!baseProduct) {
        return null;
    }

    const { productKey: baseProductKey, productLogo: BaseProductLogo } =
        baseProduct;
    const isBundle = bundledProducts.length > 0;
    const size = isBundle ? "medium" : "large";

    if (!isBundle) {
        return (
            <div css={productLogoCss}>
                <BaseProductLogo
                    appearance="brand"
                    key={baseProductKey}
                    size={size}
                />
            </div>
        );
    }

    return (
        <div css={multipleProductLogosCss}>
            <BaseProductLogo
                appearance="brand"
                key={baseProductKey}
                size={size}
            />
            {bundledProducts.map(
                ({
                    productKey: bundledProductKey,
                    productLogo: BundledProductLogo
                }) => (
                    <>
                        <span css={multipleProductLogosPlusCss}>+</span>
                        <BundledProductLogo
                            appearance="brand"
                            key={bundledProductKey}
                            size={size}
                        />
                    </>
                )
            )}
        </div>
    );
};

const containerStyle = css({
    alignItems: "stretch",
    color: N700,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
});

const productLogoCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockEnd: 20
});

const multipleProductLogosCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockEnd: 20,
    alignItems: "center",
    columnGap: 12,
    flexWrap: "wrap",
    rowGap: 4
});

const multipleProductLogosPlusCss = css({
    fontSize: 24,
    marginBlockStart: -4
});
